"use client";

import { useState, useEffect, useRef } from "react";
import { getCountryOptions, NO_COUNTRY_OPTIONS } from "@/lib/country-options";
import { useClientValue } from "@/lib/use-client-value";
import { ChevronDown, Check, Search } from "lucide-react";
import { JobsCountryOption } from "@/types/country";
import { FlagImage } from "@/components/currency/flag";

interface Props {
  value: { country: JobsCountryOption | null; phone: string };
  onChange: (v: { country: JobsCountryOption | null; phone: string }) => void;
}

export function ApplyPhoneNumberPicker({ value, onChange }: Props) {
  const countries = useClientValue(getCountryOptions, NO_COUNTRY_OPTIONS);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setKeyword("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Auto-focus search when opened
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const filtered = countries.filter((c) => {
    const kw = keyword.toLowerCase();
    return (
      c.name.toLowerCase().includes(kw) ||
      c.code.toLowerCase().includes(kw) ||
      c.callingCode.includes(kw)
    );
  });

  const selectCountry = (c: JobsCountryOption) => {
    onChange({ ...value, country: c });
    setOpen(false);
    setKeyword("");
  };

  return (
    <div ref={containerRef} className="flex">
      {/* Country selector */}
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="h-[46px] px-3 flex items-center gap-1.5 border border-brand-bg/[0.1] border-r-0 rounded-l-xl bg-white text-sm text-brand-bg hover:bg-brand-bg/[0.03] transition-colors min-w-[110px] 3xl:text-base"
        >
          {value.country ? (
            <>
              <FlagImage code={value.country.code} alt={value.country.name} />
              <span className="font-medium">+{value.country.callingCode}</span>
            </>
          ) : (
            <span className="text-brand-bg/35">Code</span>
          )}
          <ChevronDown
            className={`h-3.5 w-3.5 text-brand-bg/35 ml-auto transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 z-50 w-[280px] bg-white input-border-radius border border-brand-bg/[0.1] shadow-lg shadow-brand-bg/[0.06] overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-brand-bg/[0.07]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-bg/30 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search country..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm text-brand-bg placeholder-brand-bg/30 bg-brand-bg/[0.04] rounded-full focus:outline-none focus:ring-1 focus:ring-brand-bg/10"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-center text-sm text-brand-bg/35 py-6">No results</p>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => selectCountry(c)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-brand-bg/[0.04] transition-colors"
                  >
                    <FlagImage code={c.code} alt={c.name} />
                    <span className="flex-1 text-brand-bg truncate">{c.name}</span>
                    <span className="text-brand-bg/40 text-xs font-medium shrink-0">
                      +{c.callingCode}
                    </span>
                    {value.country?.code === c.code && (
                      <Check className="h-3.5 w-3.5 text-brand-bg/55 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Phone number input */}
      <input
        type="tel"
        value={value.phone}
        onChange={(e) => onChange({ ...value, phone: e.target.value.replace(/\D/g, "") })}
        placeholder="123453289"
        className="w-full h-[46px] rounded-r-xl bg-white border border-brand-bg/[0.1] px-4 text-sm text-brand-bg placeholder-brand-bg/30 focus:outline-none focus:border-brand-bg/25 transition-all duration-200 3xl:text-base"
      />
    </div>
  );
}
